/**
 * PGLP | FORM DE IMÓVEL — wrapper
 * ARQUIVO: /src/components/imovel/ImovelForm.jsx
 * VERSÃO: 3.4.0
 * ------------------------------------------------------------
 * FIX v3.4.0:
 *   ✔ Passa laudoIdNumerico para ImovelParadigma
 *   ✔ Permite Engine de Prazo identificar o laudo pelo ID numérico
 *
 * FIX v3.3.0:
 *   ✔ Só renderiza ImovelParadigma DEPOIS do loading terminar
 *   ✔ Usa `codigo` como fallback estável em idRegistro
 *   ✔ Evita montar o filho com dados vazios antes do fetch
 * ============================================================
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ArrowLeft, AlertCircle } from 'lucide-react';
import ImovelParadigma from './ImovelParadigma_inicial';

/* ============================================================
   VALIDAÇÃO DE UUID
============================================================ */

function uuidValido(v) {
  if (!v || typeof v !== 'string') return false;
  if (v === 'undefined' || v === 'null') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
}

/* ============================================================
   HELPERS DE NORMALIZAÇÃO
============================================================ */

function desembrulhar(campo) {
  if (campo === null || campo === undefined) return '';
  if (typeof campo === 'object' && 'valor' in campo) return campo.valor ?? '';
  if (typeof campo === 'string' || typeof campo === 'number') return campo;
  return '';
}

function normalizarImovelSalvo(json) {
  if (!json) return null;

  if (typeof json.logradouro === 'string' && json.logradouro !== '') {
    return json;
  }

  const ident = json.identificacao || {};
  const loc   = json.localizacao   || {};
  const carac = json.caracterizacao || {};
  const vinc  = json.vinculo_juridico || {};

  const lat = loc.coordenadas?.latitude  ? desembrulhar(loc.coordenadas.latitude)  : '';
  const lng = loc.coordenadas?.longitude ? desembrulhar(loc.coordenadas.longitude) : '';

  return {
    id:       json.id       || '',
    laudo_id: json.laudo_id || '',
    laudoRef: json.laudoRef || json.laudo_id || '',

    logradouro:  desembrulhar(loc.logradouro),
    numero:      desembrulhar(loc.numero),
    complemento: desembrulhar(loc.complemento),
    bairro:      desembrulhar(loc.bairro),
    cidade:      desembrulhar(loc.cidade),
    uf:          desembrulhar(loc.uf),
    cep:         desembrulhar(loc.cep),

    lat: String(lat || ''),
    lng: String(lng || ''),
    geocodificacao: loc.geocodificacao || null,

    areaTerreno: desembrulhar(carac.area)   || desembrulhar(json.areaTerreno),
    frente:      desembrulhar(carac.frente) || desembrulhar(json.frente),

    uso:              desembrulhar(ident.uso),
    tipologia:        desembrulhar(ident.tipo),
    padrao:           desembrulhar(ident.padrao),
    matricula:        desembrulhar(ident.matricula),
    rgi:              desembrulhar(ident.rgi),
    cartografico:     desembrulhar(ident.cartografico),
    idadeReal:        desembrulhar(carac.idadeReal),
    idadeImovel:      desembrulhar(carac.idadeImovel),
    vidaUtil:         desembrulhar(carac.vidaUtil),
    vidaUtilIBAPE:    carac.vidaUtilIBAPE ?? null,
    estadoConservacao:carac.estadoConservacao || 'c',

    topografia:      carac.topografia      || null,
    fatorTopografia: carac.fatorTopografia || null,

    documentos:      json.documentos      || {},
    dadosMatricula:  json.dadosMatricula  || null,
    estadoMatricula: json.estadoMatricula || null,

    requerente: {
      nome:              vinc.perito_nome      || '',
      documento:         '',
      processoJudicial:  vinc.processo         || '',
      finalidade_codigo: '',
      finalidade_nome:   vinc.finalidade       || '',
      comarca_nome:      vinc.comarca          || '',
      forum:             vinc.forum            || '',
      vara:              vinc.vara             || '',
      objetivo:          vinc.objetivo         || '',
      seloIntegridade:   `REQ-${new Date().getFullYear()}-MH`,
      statusJudicial:    'ATIVO',
    },

    _salvo_em: json._salvo_em,
    _etapa_atual: json._etapa_atual,
  };
}

function detectarEtapa(dados) {
  if (!dados) return 'REQUERENTE';
  if (dados._etapa_atual) return dados._etapa_atual;

  const req = dados.requerente || {};
  if (!req.processoJudicial) return 'REQUERENTE';
  if (!dados.logradouro)     return 'INGESTAO';
  if (!dados.areaTerreno)    return 'IDENTIFICACAO';

  const temMatricula =
    dados.documentos?.matricula?.status === 'pendente' ||
    dados.documentos?.matricula?.status === 'enviado' ||
    dados.documentos?.matricula?.file ||
    dados.documentos?.matricula?.meta;

  if (!temMatricula)         return 'DOCUMENTOS';
  if (!dados.tipologia)      return 'CARACTERIZACAO';
  return 'PLANEJAMENTO';
}

/* ============================================================
   COMPONENTE
   ============================================================ */

export default function ImovelForm() {
  const { codigo }        = useParams();
  const navigate          = useNavigate();
  const [searchParams]    = useSearchParams();
  const editParam         = searchParams.get('edit');

  const editUuid = uuidValido(editParam) ? editParam : null;

  const [laudo, setLaudo]           = useState(null);
  const [imovelExistente, setImovel] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [erro, setErro]             = useState(null);
  const [salvando, setSalvando]     = useState(false);

  /* ============================================================
     CARREGA LAUDO (+ IMÓVEL se estiver editando)
  ============================================================ */
  useEffect(() => {
    let cancelado = false;

    (async () => {
      setLoading(true);
      setErro(null);

      try {
        const rl = await fetch(
          `/api/laudos/buscar.php?codigo=${encodeURIComponent(codigo)}`,
          { credentials: 'include' }
        );
        const dl = await rl.json();
        if (!dl.success) throw new Error(dl.error || 'Laudo não encontrado');
        if (cancelado) return;

        console.log('[ImovelForm] Laudo carregado:', dl.laudo);
        setLaudo(dl.laudo);

        if (editUuid) {
          const ri = await fetch(
            `/api/imovel/carregar.php?uuid=${encodeURIComponent(editUuid)}`,
            { credentials: 'include' }
          );
          const di = await ri.json();
          if (cancelado) return;

          if (di.success) {
            const payload = di.imovel || di.dados;
            if (payload && Object.keys(payload).length > 0) {
              const normalizado = normalizarImovelSalvo(payload);
              console.log('[ImovelForm] Imóvel normalizado:', normalizado);
              setImovel(normalizado);
            } else {
              console.warn('[ImovelForm] Imóvel vazio no backend.');
            }
          } else {
            console.warn('[ImovelForm] Imóvel não encontrado:', di.erro || di.error);
          }
        }
      } catch (err) {
        if (!cancelado) setErro(err.message);
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();

    return () => { cancelado = true; };
  }, [codigo, editUuid]);

  /* ============================================================
     CONCLUI — salva imóvel
     ============================================================ */
  const handleCompleteAbertura = async ({ requerente, dadosImovel, etapaAtual }) => {
    setSalvando(true);
    setErro(null);

    try {
      const laudoRef = laudo?.codigo_mh || laudo?.codigo || codigo;
      if (!laudoRef) throw new Error('Laudo sem código identificador.');

      const imovelPayload = {
        id:       editUuid || dadosImovel.id || undefined,
        laudo_id: laudoRef,
        laudoRef,

        _etapa_atual: etapaAtual || 'PLANEJAMENTO',

        requerente: {
          nome:              requerente.nome,
          documento:         requerente.documento,
          processoJudicial:  requerente.processoJudicial,
          finalidade_codigo: requerente.finalidade_codigo,
          finalidade_nome:   requerente.finalidade_nome,
          comarca_nome:      requerente.comarca_nome,
          forum:             requerente.forum,
          vara:              requerente.vara,
          objetivo:          requerente.objetivo,
          seloIntegridade:   requerente.seloIntegridade,
        },

        logradouro:  dadosImovel.logradouro  || '',
        numero:      dadosImovel.numero      || '',
        complemento: dadosImovel.complemento || '',
        bairro:      dadosImovel.bairro      || '',
        cidade:      dadosImovel.cidade      || '',
        uf:          dadosImovel.uf          || '',
        cep:         dadosImovel.cep         || '',

        lat:            dadosImovel.lat            || '',
        lng:            dadosImovel.lng            || '',
        geocodificacao: dadosImovel.geocodificacao || null,

        areaTerreno: dadosImovel.areaTerreno || '',
        frente:      dadosImovel.frente      || '',

        matricula:    dadosImovel.matricula    || '',
        rgi:          dadosImovel.rgi          || '',
        cartografico: dadosImovel.cartografico || '',

        uso:               dadosImovel.uso              || '',
        tipologia:         dadosImovel.tipologia        || '',
        padrao:            dadosImovel.padrao           || '',
        idadeReal:         dadosImovel.idadeReal        || '',
        idadeImovel:       dadosImovel.idadeImovel      || '',
        vidaUtil:          dadosImovel.vidaUtil         || '',
        vidaUtilIBAPE:     dadosImovel.vidaUtilIBAPE    || null,
        estadoConservacao: dadosImovel.estadoConservacao|| 'c',

        topografia:      dadosImovel.topografia      || null,
        fatorTopografia: dadosImovel.fatorTopografia || null,

        documentos:      dadosImovel.documentos      || {},
        dadosMatricula:  dadosImovel.dadosMatricula  || null,
        estadoMatricula: dadosImovel.estadoMatricula || null,
        logAgendamento:  dadosImovel.logAgendamento  || [],

        _finalizado_em: new Date().toISOString(),
        _versao_schema: '3.4.0',
      };

      const resp = await fetch('/api/imovel/salvar.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imovel: imovelPayload }),
      });

      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.erro || data.error || 'Falha ao salvar imóvel.');
      }

      console.log('[ImovelForm] Salvo:', data.imovel_uuid);
      navigate(`/laudos/${codigo}/abertura`);
    } catch (err) {
      console.error('[ImovelForm] Erro:', err);
      setErro(err.message || 'Falha ao salvar imóvel.');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-blue-400" size={28} />
      </div>
    );
  }

  if (erro && !laudo) {
    return (
      <div className="text-center py-20">
        <p className="text-red-400 font-bold text-sm uppercase tracking-widest">⚠ {erro}</p>
      </div>
    );
  }

  const modoEdicao = Boolean(editUuid && imovelExistente);
  const etapaInicial = modoEdicao ? detectarEtapa(imovelExistente) : 'REQUERENTE';

  /* DEBUG: confirma que o laudo.id está chegando */
  const laudoIdNumerico = laudo?.id ? Number(laudo.id) : null;
  if (typeof window !== 'undefined' && !window.__imovelFormDebug) {
    window.__imovelFormDebug = true;
    console.log('[ImovelForm] laudoIdNumerico a passar:', laudoIdNumerico);
  }

  return (
    <div className="min-h-screen bg-[#050810]">

      <div className="max-w-7xl mx-auto p-6">
        <button
          onClick={() => navigate(`/laudos/${codigo}/abertura`)}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-xs font-black uppercase tracking-widest mb-4 transition-all"
        >
          <ArrowLeft size={16} /> Voltar para Abertura
        </button>

        {modoEdicao && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 flex items-center gap-3 mb-4">
            <span className="text-blue-400 text-[10px] font-black uppercase tracking-widest">
              ✏️ Editando imóvel existente · retomando em "{etapaInicial}"
            </span>
          </div>
        )}

        {salvando && (
          <div className="bg-amber-400/10 border border-amber-400/30 rounded-xl px-4 py-3 flex items-center gap-3 mb-4">
            <Loader2 className="animate-spin text-amber-400" size={14} />
            <span className="text-[10px] font-black text-amber-400 uppercase tracking-widest">
              Salvando imóvel no laudo…
            </span>
          </div>
        )}

        {erro && laudo && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-center gap-3 mb-4">
            <AlertCircle size={14} className="text-red-400 shrink-0" />
            <span className="text-red-400 text-[10px] font-black uppercase tracking-widest">
              {erro}
            </span>
          </div>
        )}
      </div>

      {!loading && (
        <ImovelParadigma
          idRegistro={imovelExistente?.id || codigo}
          laudoId={laudo?.codigo_mh || codigo}
          laudoIdNumerico={laudoIdNumerico}
          numeroLaudo={laudo?.codigo_mh}
          codigo={codigo}
          especialistaResponsavel={laudo?.perito_nome}
          dadosIniciais={imovelExistente || null}
          etapaInicial={etapaInicial}
          onCompleteAbertura={handleCompleteAbertura}
        />
      )}

    </div>
  );
}